// @flow
import * as React from 'react';
import {
  Box,
  ButtonBase,
  Link as MuiLink,
  Stack,
  Typography,
  Menu as MuiMenu,
  MenuItem, Divider
} from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  CaretUp, Gear, SignOut
} from '@phosphor-icons/react';
import { PropsWithChildren } from 'react';
import { keycloak } from '@coverright/shared/keycloak';
import { getRoute } from '../router';
import { AgentView, useAgent, useAgentCallAgenda } from '../../shared';
import { menuCollapsed, MenuToggleButton } from './menu-toggle-button';
import { useReactiveVar } from '@apollo/client';
import { Tooltip } from '@coverright/ui/inputs';
import { getUserName } from '@coverright/utils';

export const Menu = ({ children }: PropsWithChildren) => {
  const collapsed = useReactiveVar(menuCollapsed);
  const {dontDisturb} = useAgentCallAgenda();

  return <Box bgcolor={'#f6fafb'/*dontDisturb ? '#ffddd6' : '#f6fafb'*/}
              sx={{ position: 'fixed', zIndex: 1250, height: '100%', borderRight: '1px solid #eaeaea', transition: 'all .3s ease-in-out' }}>
    <Stack gap={1} bgcolor={'#f6fafb'} width={collapsed ? 50 : 250}
           sx={{ height: '100%', position: 'relative', transition: 'all .5s ease-in-out', overflowX: 'hidden' }}>
      <Box pt={1.5} pb={1} px={2} bgcolor={'#1C434F'} position={'relative'} height={45}>
        <Box component={'img'}
             sx={{ width: 100, height: 25, transition: 'all .5s ease-in-out', opacity: collapsed ? 0 : 1 }}
             src={'/assets/img/Logo_white.svg'} />
        <Box component={'img'} sx={{
          width: 25,
          height: 25,
          transition: 'all .5s ease-in-out',
          position: 'absolute',
          left: 12,
          opacity: collapsed ? 1 : 0
        }} src={'/assets/img/favicon.png'} />
      </Box>
      {children}

      <Box flex={1} />
      <ProfileButton />
      <MenuToggleButton />
    </Stack>
  </Box>
};

const ProfileButton = () => {
  const collapsed = useReactiveVar(menuCollapsed);
  const agent = useAgent();

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (callback?: () => void) => {
    setAnchorEl(null);
    callback && callback();
  };

  const cleanup = () => {
    localStorage.removeItem('outdatedLeadsModalShowedDate')
  }


  return <>
    <MuiMenu
      anchorEl={anchorEl}
      open={open}
      onClose={() => handleClose()}
      sx={{ '& .MuiPaper-root': { width: 220 } }}
    >
      <MenuItem onClick={() => handleClose(() => navigate(`/${getRoute('settings')}/profile`))} sx={{ gap: 1 }}>
        <Gear size={18} color="#1c434f" />
        Settings
      </MenuItem>
      <MenuItem onClick={() => handleClose(() => { cleanup(); keycloak.logout({ redirectUri: `https://coverright.com/` }) })}
                sx={{ gap: 1 }}>
        <SignOut size={18} color="#1c434f" />
        Logout
      </MenuItem>
    </MuiMenu>
    <ButtonBase onClick={handleClick} sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      cursor: 'pointer', transition: 'all .5s ease-in-out',
      m: 1, p: collapsed ? 0 : 1, borderRadius: '8px',
      '&:hover': { bgcolor: '#e9f2f5)' },
    }}>
      <AgentView agent={{ ...agent, fullName: agent ? getUserName(agent) : '' }} />
      <CaretUp size={18} color="#1c434f" weight={'bold'} />
    </ButtonBase>
  </>

}

interface ItemProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  to: string;
  component?: React.ReactNode;
  secondary?: boolean;
}

export const Group = ({ items, title }: { title?: string, items: ItemProps[] }) => {
  const collapsed = useReactiveVar(menuCollapsed);

  return <Stack p={1}>
    {collapsed && title && <Divider sx={{ mb: 2 }} />}
    {!collapsed && title && <Typography sx={{ pb: 1 }} variant={'caption'} color={'text.secondary'}>
      <b>{title}</b>
    </Typography>}
    {items.map((item, i) => (
      <Tooltip title={collapsed ? item.label : ''} key={i} placement={'right'}>
        <div>
          {item.component ? item.component :
            <GroupItem to={item.to}>
              <Box minWidth={18} height={18}>
                {(collapsed || !item.secondary) && item.icon}
              </Box>

              <Typography sx={{ opacity: collapsed ? 0 : 1,
                ml: item.secondary ? 1 : 0,
                                transition: 'opacity .5s ',
                                fontStyle: item.secondary ? 'italic' : 'normal' }}
                          variant={item.secondary ? 'caption' :'body2'}>{item.label}</Typography>
            </GroupItem>}
        </div>
      </Tooltip>
    ))}
  </Stack>
}

const GroupItem = (props: PropsWithChildren<{ to: string }>) => {
  return (
    <MuiLink
      sx={{
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        p: 1, borderRadius: '8px',
        '&:hover': { bgcolor: '#e9f2f5' },
        '&.active': { bgcolor: '#e9f2f5' },
        '&:hover:not(.active) p': { fontWeight: 500 },
        '& p': { display: 'inline-flex', ml: 1 },
        '&.active p': { fontWeight: 600 }
      }}
      component={NavLink}
      to={props.to}
      noWrap
    >
      {props.children}
    </MuiLink>
  );
};
